import type { GameState } from './types';
import { appendEvent } from './event-log';
import { ensurePlayerStats } from './stats';

/**
 * Modify a player's key count, clamping to minimum 0.
 * Emits an economy_change event and updates player stats (keysGained or keysLost).
 *
 * @param state - Game state to mutate
 * @param playerId - Player to modify
 * @param amount - Number of keys to add (positive) or remove (negative)
 * @param reason - Human-readable reason (e.g. 'gold_tile', 'hazard_drop', 'death_drop')
 * @returns The actual signed key delta after clamping and dev-only immunity.
 * @throws Error if player not found
 */
export function modifyKeys(
  state: GameState,
  playerId: string,
  amount: number,
  reason: string,
): number {
  const player = state.players.get(playerId);
  if (!player) throw new Error(`Player not found: ${playerId}`);

  if (amount < 0 && state.godModeImmunity?.get(playerId)?.noKeyLoss === true) {
    return 0;
  }

  const oldValue = player.keys;
  player.keys = Math.max(0, player.keys + amount);
  const newValue = player.keys;
  const actualDelta = newValue - oldValue;

  appendEvent(state, {
    type: 'economy_change',
    playerId,
    resource: 'keys',
    oldValue,
    newValue,
    reason,
  });

  // Update stats with actual delta (respects clamping)
  const stats = ensurePlayerStats(state, playerId);
  if (actualDelta > 0) {
    stats.keysGained += actualDelta;
  } else if (actualDelta < 0) {
    stats.keysLost += Math.abs(actualDelta);
  }

  return actualDelta;
}

/**
 * Modify a player's trophy count, clamping to minimum 0.
 * Emits an economy_change event.
 *
 * @param state - Game state to mutate
 * @param playerId - Player to modify
 * @param amount - Number of trophies to add (positive) or remove (negative)
 * @param reason - Human-readable reason (e.g. 'trophy_purchase', 'award_penalty')
 * @throws Error if player not found
 */
export function modifyTrophies(
  state: GameState,
  playerId: string,
  amount: number,
  reason: string,
): void {
  const player = state.players.get(playerId);
  if (!player) throw new Error(`Player not found: ${playerId}`);

  const oldValue = player.trophies;
  player.trophies = Math.max(0, player.trophies + amount);
  const newValue = player.trophies;

  appendEvent(state, {
    type: 'economy_change',
    playerId,
    resource: 'trophies',
    oldValue,
    newValue,
    reason,
  });
}
